import { householdsAPI, routesAPI, visitReportsAPI } from "../backend/api/api";
import { householdsActions } from "../backend/store/reducers/householdsReducer";
import { OfflineDispatch } from "./counters";
import { isOnline } from "./isOnline";
import { ROUTE_DETAIL_PARAMS } from "./routeParams";
import { QueuedRow } from "./queue";

/**
 * What a completed sync actually invalidated.
 *
 * A sync makes our cached copies wrong — but only the ones it touched. The
 * previous behaviour re-ran the entire prefetch: every route, every stop's report
 * data, the contact book and the full details of every household in the workspace,
 * because two visit reports reached the server. On cellular, in the field.
 */
export interface SyncTargets {
  routeIds: number[];
  householdIds: number[];
}

const asId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * Reads the affected route and household ids straight off the rows that replayed.
 * A row that names nothing usable is skipped rather than fetched as `undefined`.
 */
export const targetsFromRows = (rows: QueuedRow[]): SyncTargets => {
  const routeIds = new Set<number>();
  const householdIds = new Set<number>();

  for (const row of rows) {
    const payload = (row?.payload ?? {}) as any;

    switch (row?.type) {
      case "MARK_POINT_VISITED":
      case "UNMARK_POINT_VISITED": {
        // The visited flag lives on the route's own cached copy, so the route is
        // the thing to re-pull — ten stops on one route is still one request.
        const id = asId(payload.routeId);
        if (id) routeIds.add(id);
        break;
      }
      case "SUBMIT_VISIT_REPORT": {
        // The row is keyed by visit id; the household is what addresses the URLs
        // the report wizard reads. useVisitReportForm puts it on the payload.
        const id = asId(payload.data?.household_id);
        if (id) householdIds.add(id);
        break;
      }
      case "UPDATE_HOUSEHOLD_CONTACT": {
        const id = asId(payload.id);
        if (id) householdIds.add(id);
        break;
      }
      default:
        break;
    }
  }

  return { routeIds: Array.from(routeIds), householdIds: Array.from(householdIds) };
};

/**
 * Re-pulls only what the sync invalidated.
 *
 * Best-effort throughout: this is a repair pass, not a download the volunteer is
 * waiting on. One failed target must not cost the others theirs, and nothing here
 * touches the prefetch status — a targeted repair is not a snapshot refresh, and
 * showing "Downloading…" for it is what made the app look like it was constantly
 * re-downloading.
 */
export const refreshAfterSync = async (
  targets: SyncTargets,
  dispatch: OfflineDispatch
): Promise<void> => {
  for (const id of targets.routeIds) {
    if (!isOnline()) return;
    try {
      // Same params RoutePage requests, so this refreshes the entry that page
      // will actually look for rather than creating a second, unused one.
      await routesAPI.getRoute({ id, ...ROUTE_DETAIL_PARAMS } as any);
    } catch {
      // Stale visited flags until the next pass — not worth abandoning the rest.
    }
  }

  for (const id of targets.householdIds) {
    if (!isOnline()) return;
    try {
      const household: any = await householdsAPI.getHouseholdsById(id);
      const fresh = household?.data?.data;
      // Keep the downloaded copy in step too, or the offline snapshot serves the
      // pre-edit contact until the next full download — up to twelve hours away.
      if (fresh) dispatch(householdsActions.mergeHouseholdDetailAC(fresh));
    } catch {
      /* keep going */
    }

    try {
      await visitReportsAPI.getNextHouseholdVisit(id);
    } catch {
      /* keep going */
    }
  }
};
